<script lang="ts">
  import type { AnalysisState, ChartModel } from '../lib/types';
  import ChartHost from './ChartHost.svelte';

  export let state: AnalysisState;
  export let selectedSeriesCount: number;
  export let selectedMissingEvent: boolean;
  export let selectedPending: boolean;
  export let selectedUnavailable: boolean;
  export let requiresOneSeries: boolean;
  export let minimumSelectedSeries: number;
  export let domainsCompatible: boolean;
  export let requiresMatchingUnits: boolean;
  export let unitsCompatible: boolean;
  export let waveformQuantitiesCompatible: boolean;
  export let maximumTwoUnits: boolean;
  export let unitCount: number;
  export let eventModelPending: boolean;
  export let eventModelAttempted: boolean;
  export let eventModelError: string;
  export let model: ChartModel;
  export let cursorPreview: number;
  export let onCursorPreview: (cursor: number) => void;
</script>

<div class="analysis-chart" aria-live="polite">
  {#if state.selectedPlotIds.length === 0}
    <div class="analysis-placeholder">Select plot panels above to begin.</div>
  {:else if selectedMissingEvent}
    <div class="analysis-placeholder" role="status">Select an event in each chosen plot before analysing captured waveforms.</div>
  {:else if selectedPending}
    <div class="analysis-placeholder" role="status">Loading the selected {state.dataMode === 'event-waveforms' ? 'event waveforms' : 'plots'}…</div>
  {:else if selectedUnavailable || selectedSeriesCount === 0}
    <div class="analysis-placeholder" role="status">{state.dataMode === 'event-waveforms' ? 'Choose events with captured waveform samples.' : 'Choose plots that contain line-series data.'}</div>
  {:else if requiresOneSeries && selectedSeriesCount !== 1}
    <div class="analysis-placeholder" role="status">The time-frequency view analyses one event waveform at a time. Select one plot panel.</div>
  {:else if selectedSeriesCount < minimumSelectedSeries}
    <div class="analysis-placeholder" role="status">Select at least {minimumSelectedSeries} plot {minimumSelectedSeries === 1 ? 'panel' : 'panels'} for this analysis view.</div>
  {:else if !domainsCompatible}
    <div class="analysis-placeholder" role="status">The selected series use different horizontal axes. Choose series with the same time or sample domain.</div>
  {:else if requiresMatchingUnits && !unitsCompatible}
    <div class="analysis-placeholder" role="status">This analysis view requires series with the same measurement unit.</div>
  {:else if state.mode === 'event-ensemble' && !waveformQuantitiesCompatible}
    <div class="analysis-placeholder" role="status">Event ensemble requires the same quantity and phase in every selected plot.</div>
  {:else if maximumTwoUnits && unitCount > 2}
    <div class="analysis-placeholder" role="status">{state.mode === 'raw-comparison' ? 'Original values' : 'This analysis view'} can compare up to two different measurement units. Choose another series or plot combination.</div>
  {:else if eventModelPending}
    <div class="analysis-placeholder" role="status">Calculating event analysisâ€¦</div>
  {:else if eventModelAttempted && eventModelError}
    <div class="analysis-placeholder" role="alert">{eventModelError}</div>
  {:else}
    <ChartHost
      {model}
      kind={state.mode === 'time-frequency' ? 'heatmap' : state.dataMode === 'event-waveforms' ? 'waveform' : 'harmonic-trend'}
      showTitle={false}
      showSubtitle={false}
      cursor={model.xAxisType === 'datetime' ? cursorPreview : undefined}
      onCursorPreview={model.xAxisType === 'datetime' ? onCursorPreview : undefined}
      zoomEnabled={state.dataMode === 'event-waveforms' || model.xAxisType === 'datetime'}
    />
  {/if}
</div>
